import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "./beta-journey.css";
import { BetaStepDetail, type DetailResponse } from "./beta-step-detail";
import { BetaScreenPreview } from "./beta-screen-preview";

const client = axios.create({ withCredentials: true });
const endpoint = (memberId: number) =>
  `/api/secretmissions/editor/beta/${memberId}/journey/`;

interface Entry {
  id: number;
  stepId: number;
  step: string;
  stepType: string;
  enteredAt: string | null;
  exitedAt: string | null;
  event: string;
  nextStep: string | null;
  choice?: string | null;
  choiceInferred?: boolean;
}

// Group presentation records only; retain every original record for inspection.
// eslint-disable-next-line react-refresh/only-export-components -- used by tests
export function groupJourney(entries: Entry[]) {
  const groups: Array<Entry & { records: Entry[] }> = [];
  for (const entry of [...entries].reverse()) {
    // Only browser screen types belong in the player-facing timeline.
    if (!entry.stepType?.startsWith("Web")) continue;
    const last = groups[groups.length - 1];
    const previous = groups[groups.length - 2];
    const withinInteraction = (g: typeof last) =>
      !!g &&
      g.stepId === entry.stepId &&
      !!g.enteredAt &&
      !!entry.enteredAt &&
      Date.parse(entry.enteredAt) - Date.parse(g.enteredAt) >= 0 &&
      Date.parse(entry.enteredAt) - Date.parse(g.enteredAt) <= 15 * 60 * 1000;
    // Door submission can be recorded after the case animation.
    const target = withinInteraction(last)
      ? last
      : entry.stepId === 830 &&
          last?.stepId === 829 &&
          withinInteraction(previous) &&
          entry.choice
        ? previous
        : undefined;
    if (target) {
      target.records.push(entry);
      target.choice = entry.choice || target.choice;
      target.choiceInferred = entry.choice
        ? entry.choiceInferred
        : target.choiceInferred;
      target.event = entry.event || target.event;
      target.nextStep = entry.nextStep || target.nextStep;
    } else groups.push({ ...entry, records: [entry] });
  }
  return groups;
}

interface JourneyResponse {
  result: { entries: Entry[]; nextCursor: number | null };
}

// Preserve unknown events instead of guessing what the member selected.
const ACTION_LABELS: Record<string, string> = {
  SCHEDULEEVENT: "Reminder scheduled",
  NOW: "Continue now",
  CANTTALK: "Switch to text",
  GOALMET: "Ready for review",
  enter: "Entered screen",
  exit: "Left screen",
  complete: "Completed",
  skip: "Skipped",
  timeout: "Timed out",
  error: "Error occurred",
  navigate: "Navigated",
  submit: "Submitted",
  start: "Started",
  finish: "Finished",
};

function formatAction(event: string) {
  if (!event) return null;
  const label = ACTION_LABELS[event];
  if (label) return <span className="beta-journey-action">{label}</span>;
  return (
    <>
      <span className="beta-journey-action">Action recorded</span>
      <span className="beta-journey-raw">{event}</span>
    </>
  );
}

function formatTime(iso: string | null) {
  if (!iso) return "Time not recorded";
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
}

export interface BetaJourneyProps {
  memberId: number;
  name: string;
}

export function BetaJourney({ memberId, name }: BetaJourneyProps) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  // Guard stale responses: bump on expand/collapse/member change
  const reqId = useRef(0);
  // Block duplicate pagination requests
  const paginating = useRef(false);

  const reset = useCallback(() => {
    reqId.current++;
    setEntries([]);
    setCursor(null);
    setError("");
    setLoading(false);
    setLoadingMore(false);
    paginating.current = false;
  }, []);

  // Cancel on unmount
  useEffect(
    () => () => {
      reqId.current++;
    },
    [],
  );

  // Reset on member change
  /* eslint-disable react-hooks/set-state-in-effect -- intentional sync reset on dep change */
  useEffect(() => {
    reset();
    setOpen(false);
  }, [memberId, reset]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const fetchEntries = useCallback(
    async (before?: number) => {
      const isMore = before != null;
      if (isMore && paginating.current) return;
      const id = ++reqId.current;
      if (isMore) {
        paginating.current = true;
        setLoadingMore(true);
      } else {
        setEntries([]);
        setCursor(null);
        setLoading(true);
      }
      setError("");

      try {
        const params: Record<string, number> = {};
        if (before != null) params.before = before;
        const { data } = await client.get<JourneyResponse>(endpoint(memberId), {
          params,
        });
        if (id !== reqId.current) return; // stale
        if (isMore) {
          setEntries((prev) => [...prev, ...data.result.entries]);
        } else {
          setEntries(data.result.entries);
        }
        setCursor(data.result.nextCursor);
      } catch (e) {
        if (id !== reqId.current) return; // stale
        if (axios.isCancel(e)) return;
        setError(
          axios.isAxiosError(e)
            ? e.response?.data?.message || "Failed to load journey."
            : "Failed to load journey.",
        );
      } finally {
        if (id === reqId.current) {
          setLoading(false);
          setLoadingMore(false);
          paginating.current = false;
        }
      }
    },
    [memberId],
  );

  const previewId = useRef<number | null>(null);
  const [preview, setPreview] = useState<DetailResponse | null>(null);
  const [previewJourneyId, setPreviewJourneyId] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  const closeJourney = () => {
    reset();
    setOpen(false);
    setPreview(null);
    setPreviewJourneyId(null);
    previewId.current = null;
  };
  const grouped = groupJourney(entries);

  const toggle = () => {
    if (open) {
      reset();
      setOpen(false);
    } else {
      setOpen(true);
      void fetchEntries();
    }
  };

  return (
    <div className="beta-journey">
      <button
        type="button"
        className="beta-journey-toggle"
        aria-haspopup="dialog"
        onClick={toggle}
      >
        View journey
      </button>

      {open && (
        <dialog
          ref={dialogRef}
          className="beta-journey-panel beta-journey-modal"
          aria-label={`Journey for ${name}`}
          onCancel={closeJourney}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              const rect = e.currentTarget.getBoundingClientRect();
              if (
                e.clientX < rect.left ||
                e.clientX > rect.right ||
                e.clientY < rect.top ||
                e.clientY > rect.bottom
              )
                closeJourney();
            }
          }}
        >
          <div className="beta-journey-modal-heading">
            <h2>Journey for {name}</h2>
            <button
              type="button"
              onClick={closeJourney}
              aria-label="Close journey"
              autoFocus
            >
              ×
            </button>
          </div>
          <div className="beta-journey-layout">
            <div className="beta-journey-history">
              <div className="beta-journey-bar">
                <span className="beta-journey-status">
                  {loading
                    ? "Loading…"
                    : `${grouped.length} screen interactions`}
                </span>
                <button
                  type="button"
                  onClick={() => void fetchEntries()}
                  disabled={loading || loadingMore}
                  aria-label="Refresh journey"
                >
                  Refresh
                </button>
              </div>

              {error && (
                <p role="alert" className="beta-journey-error">
                  {error}
                </p>
              )}

              {!loading && !error && entries.length === 0 && (
                <p className="beta-journey-empty">
                  No recorded visits yet for this member.
                </p>
              )}

              {entries.length > 0 && (
                <>
                  <div className="beta-journey-summary">
                    <strong>Last recorded stop: {entries[0]?.step}</strong>
                    <div>{formatTime(entries[0]?.enteredAt || null)}</div>
                    <p>
                      Read each step below for the question, saved answer, and
                      next destination. Missing records do not necessarily mean
                      the player did not answer.
                    </p>
                  </div>
                  <ul className="beta-journey-list">
                    {grouped.map((e) => {
                      // Preview the first display visit; later grouped rows may only record the answer.
                      const jid = e.records[0].id;
                      return (
                        <li
                          key={e.id}
                          className={`beta-journey-item${previewJourneyId === jid ? " beta-journey-item--active" : ""}`}
                        >
                          <div className="beta-journey-step">{e.step}</div>
                          <div className="beta-journey-time">
                            {formatTime(e.enteredAt)}
                            {e.exitedAt && <> — {formatTime(e.exitedAt)}</>}
                          </div>
                          {e.choice && (
                            <div className="beta-journey-action">
                              {e.choiceInferred
                                ? "Selected (inferred from next step):"
                                : "Player answered:"}{" "}
                              {e.choice}
                            </div>
                          )}
                          {e.event && (
                            <details>
                              <summary>Recorded action</summary>
                              {formatAction(e.event)}
                            </details>
                          )}
                          {e.nextStep && (
                            <div className="beta-journey-next">
                              Then went to: {e.nextStep}
                            </div>
                          )}
                          <BetaStepDetail
                            memberId={memberId}
                            journeyId={jid}
                            onPreviewStart={() => {
                              previewId.current = jid;
                              setPreviewJourneyId(jid);
                              setPreview(null);
                            }}
                            onPreview={(data) => {
                              if (previewId.current === jid) setPreview(data);
                            }}
                          />
                          {e.records.length > 1 && (
                            <details>
                              <summary>
                                {e.records.length} combined records
                              </summary>
                              {e.records.map((record) => (
                                <div key={record.id}>
                                  {formatTime(record.enteredAt)} · Record{" "}
                                  {record.id}
                                  <BetaStepDetail
                                    memberId={memberId}
                                    journeyId={record.id}
                                    onPreviewStart={() => {
                                      previewId.current = record.id;
                                      setPreviewJourneyId(record.id);
                                      setPreview(null);
                                    }}
                                    onPreview={(data) => {
                                      if (previewId.current === record.id)
                                        setPreview(data);
                                    }}
                                  />
                                </div>
                              ))}
                            </details>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {cursor != null && (
                    <div className="beta-journey-bar" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => void fetchEntries(cursor)}
                        disabled={loadingMore}
                        aria-label="Load older journey entries"
                      >
                        {loadingMore ? "Loading…" : "Load older"}
                      </button>
                    </div>
                  )}

                  <p className="beta-journey-note">
                    Shown oldest to newest. Related screen records are combined
                    for display only. Original records are available under
                    combined records.
                  </p>
                </>
              )}
            </div>
            <aside className="beta-phone-side" aria-label="Screen preview">
              <div className="beta-phone-frame">
                <div className="beta-phone-notch" />
                {preview ? (
                  <BetaScreenPreview preview={preview} />
                ) : (
                  <div className="beta-phone-empty">
                    {previewJourneyId
                      ? "Loading preview\u2026"
                      : "Select \u201cRead questions & answers\u201d on a step to preview it here."}
                  </div>
                )}
              </div>
              <p className="beta-phone-caption">
                Reconstructed from current step options, not a historical
                capture.
              </p>
            </aside>
          </div>
        </dialog>
      )}
    </div>
  );
}
